import { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Bot, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { MessageBubble, type MessageEntry } from "@/components/ChatTranscript";

type SessionInfo = {
  id: string;
  repo: string;
  agentName: string;
  title?: string;
  status: "idle" | "working";
};

function Composer({ sessionId, onSent }: { sessionId: string; onSent: () => void }) {
  const [prompt, setPrompt] = useState("");
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!prompt.trim() || sending) return;
    setSending(true);
    try {
      await fetch(`/api/sessions/${sessionId}/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prompt: prompt.trim() }),
      });
      setPrompt("");
      onSent();
    } finally {
      setSending(false);
    }
  };

  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send();
  };

  return (
    <div className="flex gap-2 border-t border-border bg-background px-4 py-3">
      <Textarea
        value={prompt}
        onChange={e => setPrompt(e.target.value)}
        onKeyDown={handleKey}
        placeholder="Ask the agent… (⌘↵ to send)"
        rows={3}
        className="resize-none text-sm"
      />
      <Button onClick={send} disabled={!prompt.trim() || sending} size="sm" className="self-end">
        <Send className="h-4 w-4" />
      </Button>
    </div>
  );
}

export default function SessionChat() {
  const { id } = useParams<{ id: string }>();
  const [session, setSession] = useState<SessionInfo | null>(null);
  const [messages, setMessages] = useState<MessageEntry[]>([]);
  const bottomRef = useRef<HTMLDivElement>(null);

  const fetchMessages = useCallback(async () => {
    if (!id) return;
    const res = await fetch(`/api/sessions/${id}/transcript`);
    if (res.ok) {
      const data = await res.json();
      setMessages(data.messages ?? []);
    }
  }, [id]);

  useEffect(() => {
    if (!id) return;
    fetch(`/api/sessions/${id}`).then(r => r.ok ? r.json() : null).then(d => d && setSession(d));
    fetchMessages();
  }, [id, fetchMessages]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  if (!session) return <div className="p-8 text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="flex h-screen flex-col">
      {/* Header */}
      <div className="flex items-center gap-3 border-b border-border px-6 py-3">
        <div className="flex-1">
          <p className="text-sm font-medium">{session.title || session.repo}</p>
          <p className="text-xs text-muted-foreground">{session.agentName}</p>
        </div>
        <Badge variant={session.status === "working" ? "default" : "secondary"}>
          {session.status}
        </Badge>
      </div>

      {/* Transcript */}
      <ScrollArea className="flex-1 px-6 py-4">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-16 text-center">
            <Bot className="h-8 w-8 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">No messages yet. Type something below.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {messages.map(m => <MessageBubble key={m.id} msg={m} />)}
          </div>
        )}
        <div ref={bottomRef} />
      </ScrollArea>

      {/* Composer */}
      <Composer sessionId={session.id} onSent={fetchMessages} />
    </div>
  );
}
